import { useState } from 'react'
import { EmptyState, ErrorBanner, Metric } from '@/components/AppLayout'
import { useAsync } from '@/hooks/useAsync'
import { csvFilename, downloadCsv, toCsv } from '@/lib/csv'
import { buildMatchMailMerge, mailMergeColumns } from '@/lib/matchExport'
import {
  getLatestRun,
  getMatchRateByGender,
  getMatchPairs,
  getTopSelected,
  listAttendees,
  listOutcomes,
  listSubmissions,
  runMatchmaking,
} from '@/lib/queries'
import { groupBySubmitter } from '@/lib/submissionPicks'
import { buildUnmatchedExport, unmatchedColumns } from '@/lib/unmatchedExport'
import { useEventContext } from './eventContext'

export function ResultsPage() {
  const { event } = useEventContext()
  const run = useAsync(() => getLatestRun(event.id), [event.id])
  const pairs = useAsync(() => getMatchPairs(event.id), [event.id])
  const topSelected = useAsync(() => getTopSelected(event.id, 5), [event.id])
  const genderRates = useAsync(() => getMatchRateByGender(event.id), [event.id])
  const attendees = useAsync(() => listAttendees(event.id), [event.id])
  // Two jobs: warning about duplicates before a run, and supplying the address
  // each person confirmed on the night for the exports.
  const submissions = useAsync(() => listSubmissions(event.id), [event.id])

  // Someone who filled the form in twice has an answer that will be ignored.
  // That is the correct behaviour, but it is a poor thing to discover from the
  // results, so it is said before the button rather than after.
  const repeatSubmitters = [...groupBySubmitter(submissions.data ?? []).values()].filter(
    (entries) => entries.length > 1
  ).length

  // Built here as well as at download time so the page can warn about gaps
  // before the organiser sends anything.
  const mailMerge = buildMatchMailMerge(pairs.data ?? [])

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const latest = run.data

  const outcomes = useAsync(
    () => (latest === null ? Promise.resolve([]) : listOutcomes(latest.id)),
    [latest?.id]
  )

  async function handleRun() {
    // Re-running discards the previous run and recomputes from the current
    // submissions, so say so before doing it.
    if (latest !== null) {
      const confirmed = window.confirm(
        'Run matchmaking again? This replaces the previous results with a fresh calculation from the current submissions.'
      )
      if (!confirmed) return
    }

    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const summary = await runMatchmaking(event.id)
      setNotice(
        `Found ${String(summary.total_matches)} ${summary.total_matches === 1 ? 'match' : 'matches'} from ${String(summary.total_submissions)} ${summary.total_submissions === 1 ? 'person' : 'people'}.`
      )
      run.reload()
      pairs.reload()
      topSelected.reload()
      genderRates.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Matchmaking failed')
    } finally {
      setBusy(false)
    }
  }

  // Everyone who filled the form in but matched with nobody — the
  // thanks-for-coming list. Built once and used for both the table and the
  // download, so the file can never disagree with what is on screen.
  const unmatched = buildUnmatchedExport(
    outcomes.data ?? [],
    attendees.data ?? [],
    submissions.data ?? []
  )

  return (
    <div className="stack">
      {error !== null && <ErrorBanner message={error} />}
      {notice !== null && <p className="banner banner-success">{notice}</p>}

      {repeatSubmitters > 0 && (
        <p className="banner banner-warning">
          {repeatSubmitters} {repeatSubmitters === 1 ? 'person' : 'people'} filled the form in more
          than once. Only each person&rsquo;s newest answer will be used. Check the extra entries on
          the Form tab first if you are not sure the right one is on top.
        </p>
      )}

      <div className="row btn-group-stack">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => void handleRun()}
          disabled={busy}
        >
          {busy ? 'Running…' : latest === null ? 'Run matchmaking' : 'Re-run matchmaking'}
        </button>
        <button
          className="btn"
          type="button"
          disabled={mailMerge.rows.length === 0}
          onClick={() => {
            downloadCsv(
              csvFilename(event.name, 'matches'),
              toCsv(mailMerge.rows, mailMergeColumns(mailMerge.matchColumns))
            )
          }}
        >
          Download matches CSV
        </button>
        <button
          className="btn"
          type="button"
          disabled={unmatched.rows.length === 0}
          onClick={() => {
            downloadCsv(
              csvFilename(event.name, 'no-matches'),
              toCsv(unmatched.rows, unmatchedColumns)
            )
          }}
        >
          Download no-matches CSV
        </button>
      </div>

      {run.loading ? (
        <p className="page-status">Loading results…</p>
      ) : latest === null ? (
        <div className="card">
          <EmptyState
            title="No results yet"
            description="Once the form has closed and people have submitted, run the matchmaker to find mutual matches."
          />
        </div>
      ) : (
        <>
          <div className="metric-grid">
            <Metric label="Matches" value={latest.total_matches} />
            <Metric label="Match rate" value={`${String(latest.match_rate)}%`} />
            {/* People, not entries: someone who filled the form in twice is one
                person, and this is the match-rate denominator. */}
            <Metric label="People who submitted" value={latest.total_submissions} />
            <Metric label="Matched people" value={latest.matched_attendees} />
          </div>
          <p className="field-hint">
            Match rate is the share of people who submitted the form and got at least one match. Run
            at {new Date(latest.run_at).toLocaleString()}.
          </p>
          <p className="field-hint">
            The matches CSV is one row per person for a mail merge: their first name, the address to
            send to, and each match&rsquo;s name and phone number. A pair appears twice — once from
            each side. The no-matches CSV is the other half of the evening, in the same shape so one
            template can send both.
          </p>

          {mailMerge.missingContact.length > 0 && (
            <p className="banner banner-warning">
              {mailMerge.missingContact.length}{' '}
              {mailMerge.missingContact.length === 1 ? 'match has' : 'matches have'} no phone
              number, so the Contact column would be empty for{' '}
              {mailMerge.missingContact
                .slice(0, 3)
                .map((m) => m.recipient)
                .join(', ')}
              {mailMerge.missingContact.length > 3 &&
                ` and ${String(mailMerge.missingContact.length - 3)} more`}
              . Add the numbers on the Attendees tab before sending.
            </p>
          )}

          <div className="card">
            <div className="card-header">
              <h2>Matches</h2>
              <span className="badge">{(pairs.data ?? []).length}</span>
            </div>

            {(pairs.data ?? []).length === 0 ? (
              <p className="field-hint">No mutual matches this time.</p>
            ) : (
              <div className="table-responsive">
                <div className="table-wrap">
                  <table>
                    <caption className="sr-only">Mutual matches with contact details</caption>
                    <thead>
                      <tr>
                        <th scope="col">Person A</th>
                        <th scope="col">Person B</th>
                        <th scope="col">Contact A</th>
                        <th scope="col">Contact B</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pairs.data ?? []).map((pair) => (
                        <tr key={pair.match_id}>
                          <td data-label="Person A">
                            <span className="cell-number">{pair.a_matchmaking_id}</span>{' '}
                            {pair.a_first_name} {pair.a_last_name ?? ''}
                          </td>
                          <td data-label="Person B">
                            <span className="cell-number">{pair.b_matchmaking_id}</span>{' '}
                            {pair.b_first_name} {pair.b_last_name ?? ''}
                          </td>
                          <td data-label="Contact A" className="cell-muted">
                            {pair.a_email ?? '—'}
                          </td>
                          <td data-label="Contact B" className="cell-muted">
                            {pair.b_email ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h2>No matches</h2>
              <span className="badge">{unmatched.rows.length}</span>
            </div>
            <p className="field-hint">
              Submitted the form but matched with nobody. These are the people who get the
              thanks-for-coming email, and they are exactly what the no-matches CSV contains. Anyone
              who never filled the form in is not here: they were not turned down, they did not take
              part.
            </p>

            {unmatched.missingEmail.length > 0 && (
              <p className="banner banner-warning">
                {unmatched.missingEmail.length}{' '}
                {unmatched.missingEmail.length === 1 ? 'person has' : 'people have'} no email
                address on file, so the Recipient column would be empty for{' '}
                {unmatched.missingEmail
                  .slice(0, 3)
                  .map((row) => `${row.firstName} (${row.matchmakingId})`)
                  .join(', ')}
                {unmatched.missingEmail.length > 3 &&
                  ` and ${String(unmatched.missingEmail.length - 3)} more`}
                . Add the addresses on the Attendees tab before sending.
              </p>
            )}

            {unmatched.rows.length === 0 ? (
              <p className="field-hint">Everyone who submitted got at least one match.</p>
            ) : (
              <div className="table-responsive">
                <div className="table-wrap">
                  <table>
                    <caption className="sr-only">People who submitted but did not match</caption>
                    <thead>
                      <tr>
                        <th scope="col">No.</th>
                        <th scope="col">Name</th>
                        <th scope="col">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatched.rows.map((row) => (
                        <tr key={row.matchmakingId}>
                          <td data-label="No." className="cell-number">
                            {row.matchmakingId}
                          </td>
                          <td data-label="Name">
                            {row.firstName} {row.lastName}
                          </td>
                          <td data-label="Email" className="cell-muted">
                            {row.recipient === '' ? '—' : row.recipient}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Match rate by gender</h2>
            </div>
            <p className="field-hint">
              Share of each group who submitted the form and got at least one match — the same
              denominator as the headline figure. Anyone who did not answer the question is grouped
              as <em>unspecified</em>, so the rows always add up to the submissions.
            </p>

            {(genderRates.data ?? []).length === 0 ? (
              <p className="field-hint">Nobody has submitted yet.</p>
            ) : (
              <div className="table-responsive">
                <div className="table-wrap">
                  <table>
                    <caption className="sr-only">Match rate broken down by gender</caption>
                    <thead>
                      <tr>
                        <th scope="col">Gender</th>
                        <th scope="col">Submitted</th>
                        <th scope="col">Matched</th>
                        <th scope="col">Match rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(genderRates.data ?? []).map((row) => (
                        <tr key={row.gender}>
                          <td data-label="Gender">{row.gender}</td>
                          <td data-label="Submitted">{row.submitters}</td>
                          <td data-label="Matched">{row.matched}</td>
                          <td data-label="Match rate">{row.match_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <h2>Most picked</h2>
            {(topSelected.data ?? []).length === 0 ? (
              <p className="field-hint">Nobody has been picked yet.</p>
            ) : (
              <div className="table-responsive">
                <div className="table-wrap">
                  <table>
                    <caption className="sr-only">Most selected people, grouped by gender</caption>
                    <thead>
                      <tr>
                        <th scope="col">Gender</th>
                        <th scope="col">No.</th>
                        <th scope="col">Name</th>
                        <th scope="col">Times picked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(topSelected.data ?? []).map((row) => (
                        <tr key={row.attendee_id}>
                          <td data-label="Gender" className="cell-muted">
                            {row.gender}
                          </td>
                          <td data-label="No." className="cell-number">
                            {row.matchmaking_id}
                          </td>
                          <td data-label="Name">
                            {row.first_name} {row.last_name ?? ''}
                          </td>
                          <td data-label="Times picked">{row.times_selected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
